import React, { useEffect, useRef, useCallback } from 'react';
import ElasticSearchAPIConnector from '@elastic/search-ui-elasticsearch-connector';
import {
  ErrorBoundary,
  SearchProvider,
  WithSearch,
} from '@elastic/react-search-ui';
import {
  Layout,
} from '@elastic/react-search-ui-views';
import SearchHeader from '../header/SearchHeader';
import MobileFilterMenu from '../mobile/MobileFilterMenu';
import FilterSummary from '../facet/FilterSummary';
import CustomPaginationView from '../pagination/CustomPaginationView';
import { MobileSearchProvider } from '../mobile/MobileSearchContext';

import FacetContainer from '../customFacet';
import { ElasticResultsWrapper } from '../job/ElasticResultsWrapper';

const ELASTIC_CONFIG = {
  host: window.aqJobsOptions?.elasticHost || '',
  apiKey: window.aqJobsOptions?.elasticAPIKey || '',
  jobsIndex: window.aqJobsOptions?.elasticJobIndex || '',
};

const connector = new ElasticSearchAPIConnector({
  host: ELASTIC_CONFIG.host,
  index: ELASTIC_CONFIG.jobsIndex,
  apiKey: ELASTIC_CONFIG.apiKey,
});

const config = {
  searchQuery: {
    search_fields: {
      job_title: { weight: 5 }, // prioritize job_title
      job_description: { weight: 1 },
    },
    result_fields: {
      job_title: { raw: {} },
      job_description: { snippet: {} },
      location: { raw: {} },
      salary: { raw: {} },
      job_id: { raw: {} },
      posted_date: { raw: {} },
      offsite_preference_name: { raw: {} },
      friendly_salary: { raw: {} },
    },
    disjunctiveFacets: [
      'markets.enum',
      'global_filter_category.enum',
      'placement_types.enum',
      'offsite_pref_filter_option.enum',
    ],
    facets: {
      'markets.enum': {
        type: 'value', sort: 'value', direction: 'asc', size: 1000,
      },
      'global_filter_category.enum': { type: 'value', size: 1000 },
      'placement_types.enum': { type: 'value' },
      'offsite_pref_filter_option.enum': { type: 'value' },
    },
  },
  autocompleteQuery: {
    results: {
      search_fields: { job_title: { weight: 3 } },
      result_fields: {
        job_title: { snippet: { size: 100, fallback: true } },
      },
    },
  },
  apiConnector: connector,
  alwaysSearchOnInitialLoad: true,
};

const JobListingMain = () => {
  const resultsContainerRef = React.useRef<HTMLDivElement>(null);
  const scrollToResultsTop = useCallback(() => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, []);

  return (
    <SearchProvider config={config}>
      <MobileSearchProvider>
        <WithSearch
          mapContextToProps={({
            resultsPerPage,
            setResultsPerPage,
            setSearchTerm,
            searchTerm,
            setSort,
          }) => ({
            resultsPerPage,
            setResultsPerPage,
            setSearchTerm,
            searchTerm,
            setSort,
          })}
        >
          {({
            resultsPerPage,
            setResultsPerPage,
            setSearchTerm,
            searchTerm,
            setSort,
          }) => {
            useEffect(() => {
              if (resultsPerPage !== 12) setResultsPerPage(12);

              // Handle default sort
              if (!searchTerm) {
                // Initial load: show newest job first
                setSort([{ field: 'posted_date', direction: 'desc' }]);
              } else {
                // When searching: relevance first, then date. May have to revisit
                setSort([
                  { field: '_score', direction: 'desc' },
                  { field: 'posted_date', direction: 'desc' },
                ]);
              }
            }, [resultsPerPage, setResultsPerPage, searchTerm, setSort]);

            return (
              <div className="job-search">
                <SearchHeader setSearchTerm={setSearchTerm} searchTerm={searchTerm} />
                <ErrorBoundary>
                  <Layout
                    header={<MobileFilterMenu />}
                    sideContent={
                      <div className="job-listing-sidebar">
                        <FilterSummary />
                        <FacetContainer key={'2'} field={'placement_types.enum'} label={'Placement type'} onFilterChange={scrollToResultsTop} />
                        <FacetContainer key={'4'} field={'offsite_pref_filter_option.enum'} label={'Location preference'} onFilterChange={scrollToResultsTop} />
                        <FacetContainer key={'1'} field={'markets.enum'} label={'Location'} onFilterChange={scrollToResultsTop} />
                      </div>
                    }
                    bodyContent={
                      <div className="job-listing-body-results" ref={resultsContainerRef}>
                        <ElasticResultsWrapper />
                      </div>
                    }
                    bodyFooter={
                      <div className="job-listing-body-footer">
                        <CustomPaginationView
                          pagingContainer="pagination"
                          resultsContainerRef={resultsContainerRef}
                        />
                      </div>
                    }
                  />
                </ErrorBoundary>
              </div>
            );
          }}
        </WithSearch>
      </MobileSearchProvider>
    </SearchProvider>
  );
};

export { JobListingMain };
